import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTestOrderResults,
  submitTestOrderResults,
} from "../../store/slices/testOrderSlice";
import toast from "../../utils/toast";
import { fetchQuestionsByTest } from "../../store/slices/dynamicQuestionSlice";

const ResultEntryForm = React.forwardRef(({
  order,
  onClose,
  autoCloseOnSave = true,
  onSaved,
  onActionStateChange,
  showActions = true,
  showPatientContext = true,
  testParameters = [],
  testDetails,
  dynamicQuestionsInfo,
  findReferenceRange,
}, ref) => {
  const dispatch = useDispatch();
  const { testOrderResultsById, loading: orderResultsLoading } = useSelector(
    (s) => s.testOrders || { testOrderResultsById: {}, loading: false }
  );
  const { user } = useSelector((state) => state.auth || {});

  const loading = orderResultsLoading;

  const ui = useMemo(() => {
    const inputBase =
      "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm " +
      "focus:outline-none focus:ring-2 focus:ring-[#36F1A2] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500";
    const selectBase =
      "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm " +
      "focus:outline-none focus:ring-2 focus:ring-[#36F1A2] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500";
    const textareaBase =
      "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm " +
      "focus:outline-none focus:ring-2 focus:ring-[#36F1A2] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500";
    const card = "rounded-2xl border border-gray-100 bg-white shadow-sm";
    const cardHeader = "px-5 py-4 border-b border-gray-100";
    const cardBody = "p-5";
    const sectionTitle = "text-sm font-semibold text-[#235F72] tracking-wide";
    const sectionHint = "mt-1 text-xs text-gray-500";
    return { inputBase, selectBase, textareaBase, card, cardHeader, cardBody, sectionTitle, sectionHint };
  }, []);

  const [mainResult, setMainResult] = useState({
    result_value: "",
    numeric_value: "",
    unit: "",
    status: "normal",
    comments: "",
    interpretation: "",
    is_verified: false,
  });

  // NOTE: Avoid locking results after completion/finalize for now.
  const isLocked = false;

  const [parameterResults, setParameterResults] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const lastHydratedScopedResultsRef = useRef(null);
  const isDirtyRef = useRef(false);
  const markDirty = () => {
    isDirtyRef.current = true;
  };
  const clearDirty = () => {
    isDirtyRef.current = false;
  };

  const [selectedRangeIdByParamId, setSelectedRangeIdByParamId] = useState({});
  const [manualRangeByParamId, setManualRangeByParamId] = useState({});
  const { questionsByTest = {} } = useSelector(
    (s) => s.dynamicQuestions || { questionsByTest: {}, loading: false }
  );

  const [criticalConfirmOpen, setCriticalConfirmOpen] = useState(false);
  const testId = order?.test_id || testDetails?.id;
  useEffect(() => {
    if (testId && !questionsByTest[testId]) {
      dispatch(fetchQuestionsByTest(testId));
    }
  }, [dispatch, testId, questionsByTest]);

  const questionMap = useMemo(() => {
    const list = Array.isArray(questionsByTest[testId])
      ? questionsByTest[testId]
      : [];
    const map = {};
    for (const q of list) {
      if (!q) continue;
      map[String(q.id)] = q;
    }
    return map;
  }, [questionsByTest, testId]);

  // Fetch existing results
  useEffect(() => {
    if (order?.id) {
      dispatch(fetchTestOrderResults(order.id));
    }
  }, [dispatch, order?.id]);

  // Process existing results when loaded
  // IMPORTANT: only hydrate from results scoped to this order.
  // In the visit modal multiple ResultEntryForms are mounted; other forms can fetch results
  // and update the shared `testOrderResults` slice, which must NOT overwrite local edits.
  useEffect(() => {
    if (order?.id == null) return;

    const scoped = testOrderResultsById?.[String(order.id)];
    if (!scoped || typeof scoped !== "object") return;

    // If the user has typed/changed anything locally, do not let late fetches
    // overwrite in-progress edits.
    if (isDirtyRef.current) return;

    // Removed justSavedRef logic - we now handle draft saves differently in saveResults

    // Avoid overwriting user edits when other orders update the global map.
    // Redux updates other keys can change the parent object reference, but the scoped
    // object reference stays stable unless this order's results were actually updated.
    if (lastHydratedScopedResultsRef.current === scoped) return;
    lastHydratedScopedResultsRef.current = scoped;

    const results = scoped?.test_result !== undefined ? scoped : scoped?.data;
    const testResultRow = results?.test_result || null;
    const parameterRows = Array.isArray(results?.parameter_results)
      ? results.parameter_results
      : [];

    if (testResultRow) {
      setMainResult({
        result_value: testResultRow.result_value || "",
        numeric_value:
          testResultRow.numeric_value === null || testResultRow.numeric_value === undefined
            ? ""
            : String(testResultRow.numeric_value),
        unit: testResultRow.unit || "",
        status: testResultRow.status || "normal",
        comments: testResultRow.comments || "",
        interpretation: testResultRow.interpretation || "",
        is_verified: Boolean(testResultRow.is_verified),
      });
    }

    const paramResults = {};
    for (const pr of parameterRows) {
      const pid = pr?.parameter_id;
      if (!pid) continue;
      const v =
        pr.string_value !== null && pr.string_value !== undefined
          ? String(pr.string_value)
          : pr.result_value !== null && pr.result_value !== undefined
            ? String(pr.result_value)
            : "";
      paramResults[pid] = {
        id: pr.id,
        value: v,
        unit: pr.unit || "",
        status: pr.status || "normal",
        comments: pr.comments || "",
      };
    }

    // IMPORTANT: Merge fetched data with existing local state instead of replacing.
    // This prevents losing parameters when the fetch returns incomplete data (e.g., 
    // only first parameter due to race condition or backend issue).
    // Only update parameters that were actually fetched from the server.
    if (parameterRows.length > 0) {
      setParameterResults((prev) => {
        // Merge: keep existing local state, but update with fetched data where available
        const merged = { ...prev };
        for (const pid in paramResults) {
          merged[pid] = paramResults[pid];
        }
        return merged;
      });
    }
  }, [testOrderResultsById, order?.id]);

  // Initialize parameter results for all test parameters
  useEffect(() => {
    if (testParameters.length > 0) {
      const initialResults = {};
      testParameters.forEach((param) => {
        if (!parameterResults[param.id]) {
          initialResults[param.id] = {
            value: "",
            numeric_value: "",
            unit: param.unit || "",
            status: "unknown",
            comments: "",
          };
        }
      });
      if (Object.keys(initialResults).length > 0) {
        setParameterResults((prev) => ({ ...prev, ...initialResults }));
      }
    }
  }, [testParameters, parameterResults]);

  // Initialize selected reference ranges (default = applicable range chosen by age/sex/sample)
  useEffect(() => {
    if (!Array.isArray(testParameters) || testParameters.length === 0) return;
    setSelectedRangeIdByParamId((prev) => {
      const next = { ...prev };
      for (const p of testParameters) {
        const existing = next[p.id];
        if (existing !== undefined && existing !== null && String(existing) !== "") continue;
        const defaultId = p?.applicable_reference_range?.id ?? p?.reference_ranges?.[0]?.id ?? "";
        next[p.id] = defaultId === null || defaultId === undefined ? "" : String(defaultId);
      }
      return next;
    });
  }, [testParameters]);

  // Reset form function
  const resetForm = () => {
    clearDirty();
    lastHydratedScopedResultsRef.current = null;
    setMainResult({
      result_value: "",
      numeric_value: "",
      unit: "",
      status: "normal",
      comments: "",
      interpretation: "",
      is_verified: false,
    });
    setParameterResults({});
  };

  const hasOrder = Boolean(order);

  // Calculate status based on reference range and value
  const getActiveReferenceRange = (parameter) => {
    const list = Array.isArray(parameter?.reference_ranges)
      ? parameter.reference_ranges
      : [];
    const selectedId = selectedRangeIdByParamId?.[parameter?.id];
    if (selectedId) {
      const found = list.find((r) => String(r?.id) === String(selectedId));
      if (found) return found;
    }
    return parameter?.applicable_reference_range || null;
  };

  const calculateParameterStatus = (parameter, value) => {
    if (!value || value === "" || value === null) return "unknown";

    const trimmedValue = String(value).trim();
    const numericPortion = trimmedValue.replace(/^[<>]\s*/, "");
    const numValue = parseFloat(numericPortion);
    if (isNaN(numValue)) return "unknown";

    const referenceRange = getActiveReferenceRange(parameter);
    if (!referenceRange) return "unknown";

    const { min_value, max_value, critical_low, critical_high } =
      referenceRange;

    if (critical_low !== null && numValue <= critical_low) return "critical";
    if (critical_high !== null && numValue >= critical_high) return "critical";
    if (min_value !== null && numValue < min_value) return "low";
    if (max_value !== null && numValue > max_value) return "high";

    return "normal";
  };

  const isAcceptedNumericResult = (value) => {
    const trimmed = String(value ?? "").trim();
    return (
      trimmed === "" ||
      /^-?\d*\.?\d*$/.test(trimmed) ||
      /^[<>]\s*-?\d*\.?\d*$/.test(trimmed)
    );
  };

  const isSpecialNumericResult = (value) => /^[<>]\s*-?\d*\.?\d+$/.test(String(value ?? "").trim());

  // Auto-calculate status when value changes - FIXED INPUT HANDLING
  const updateParameterResultValue = (parameterId, field, value) => {
    markDirty();
    const parameter = testParameters.find((p) => p.id === parameterId);
    const activeRange = parameter ? getActiveReferenceRange(parameter) : null;

    const dataType = parameter?.data_type || "numeric";
    const normalized = value;
    const hasValue = normalized !== null && normalized !== undefined && String(normalized).trim() !== "";

    const nextStatus =
      field === "value"
        ? !hasValue
          ? "unknown"
          : dataType === "numeric"
            ? calculateParameterStatus(parameter, normalized)
            : "normal"
        : undefined;

    setParameterResults((prev) => ({
      ...prev,
      [parameterId]: {
        ...prev[parameterId],
        [field]: value,
        // Only update status and unit if it's the value field and value is not empty
        ...(field === "value" && {
          status: nextStatus,
          unit:
            activeRange?.unit ||
            parameter?.unit ||
            "",
        }),
      },
    }));
  };

  // Get status style with colors and icons
  const getStatusStyle = (status) => {
    const styles = {
      normal: {
        color: "text-emerald-700",
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        icon: "✓",
        label: "Normal",
      },
      low: {
        color: "text-amber-700",
        bg: "bg-amber-50",
        border: "border-amber-200",
        icon: "↓",
        label: "Low",
      },
      high: {
        color: "text-rose-700",
        bg: "bg-rose-50",
        border: "border-rose-200",
        icon: "↑",
        label: "High",
      },
      critical: {
        color: "text-red-800",
        bg: "bg-red-50",
        border: "border-red-200",
        icon: "!",
        label: "Critical",
      },
      unknown: {
        color: "text-gray-600",
        bg: "bg-gray-50",
        border: "border-gray-200",
        icon: "·",
        label: "Not entered",
      },
      provided: {
        color: "text-[#085DB6]",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: "•",
        label: "Provided",
      },
    };
    return styles[status] || styles.unknown;
  };

  const getDisplayStatus = (param, result) => {
    const dt = param?.data_type || "numeric";
    const hasValue = result?.value !== null && result?.value !== undefined && String(result.value).trim() !== "";
    if (!hasValue) return "unknown";
    if (dt === "numeric") return result?.status || "unknown";
    return "provided";
  };

  // Format reference range for display
  const formatReferenceRange = (parameter) => {
    const range = getActiveReferenceRange(parameter);
    if (!range) return "No reference range available";

    const { min_value, max_value, unit, critical_low, critical_high } = range;

    let rangeText = "";
    if (min_value !== null && max_value !== null) {
      rangeText = `${min_value} - ${max_value} ${unit || ""}`;
    } else if (min_value !== null) {
      rangeText = `> ${min_value} ${unit || ""}`;
    } else if (max_value !== null) {
      rangeText = `< ${max_value} ${unit || ""}`;
    } else {
      return "No reference values";
    }

    if (critical_low !== null || critical_high !== null) {
      rangeText += ` (Critical: `;
      if (critical_low !== null) rangeText += `<${critical_low}`;
      if (critical_low !== null && critical_high !== null) rangeText += " or ";
      if (critical_high !== null) rangeText += `>${critical_high}`;
      rangeText += `)`;
    }

    return rangeText;
  };

  const rangeLabel = (r) => {
    if (!r) return "";
    const ageUnit = r.age_unit || "years";
    const ageText =
      r.min_age != null || r.max_age != null
        ? `Age ${r.min_age ?? "-"}-${r.max_age ?? "-"} ${ageUnit}`
        : "Age -";
    const genderText = r.gender ? `Gender ${r.gender}` : "Gender -";
    const unitText = r.unit ? ` ${r.unit}` : "";
    if (r.min_value != null && r.max_value != null) return `${genderText} • ${ageText} • ${r.min_value}-${r.max_value}${unitText}`;
    if (r.min_value != null) return `${genderText} • ${ageText} • >${r.min_value}${unitText}`;
    if (r.max_value != null) return `${genderText} • ${ageText} • <${r.max_value}${unitText}`;
    return `${genderText} • ${ageText}`;
  };

  const setSelectedReferenceRange = (parameter, rangeId) => {
    const pid = parameter?.id;
    if (!pid) return;

    markDirty();

    setSelectedRangeIdByParamId((prev) => ({ ...prev, [pid]: String(rangeId || "") }));
    setManualRangeByParamId((prev) => ({ ...prev, [pid]: true }));

    // Recalculate status/unit for existing value
    const currentValue = parameterResults?.[pid]?.value;
    const hasValue = currentValue != null && String(currentValue).trim() !== "";
    if (hasValue) {
      const dt = parameter?.data_type || "numeric";
      const nextStatus = dt === "numeric" ? calculateParameterStatus(parameter, currentValue) : "normal";
      const selectedRange = (parameter?.reference_ranges || []).find((r) => String(r?.id) === String(rangeId)) || null;
      setParameterResults((prev) => ({
        ...prev,
        [pid]: {
          ...prev[pid],
          status: nextStatus,
          unit: selectedRange?.unit || prev?.[pid]?.unit || parameter?.unit || "",
        },
      }));
    }
  };

  // Save results to both test_results and parameter_results - FIXED DATA SUBMISSION
  const saveResults = async (isFinal = false) => {
    if (isSubmitting) return;
    if (isLocked) {
      toast.error("This result is locked and cannot be modified.");
      return;
    }

    setIsSubmitting(true);

    const debugEnabled =
      (process.env.NODE_ENV || "development") === "development" &&
      (typeof window !== "undefined" &&
        window?.localStorage?.getItem("debugResultsSave") === "1");

    try {
      // Validate on finalize: parameters are optional, but at least ONE must be provided (and numeric must be valid)
      if (isFinal) {
        if (testParameters.length > 0) {
          const provided = testParameters.filter((p) => {
            const v = parameterResults?.[p.id]?.value;
            return v !== null && v !== undefined && String(v).trim() !== "";
          });

          if (provided.length === 0) {
            toast.error("Enter at least one parameter result before finalizing.");
            return;
          }

          const invalidNumeric = provided
            .filter((p) => (p.data_type || "numeric") === "numeric")
            .filter((p) => {
              const v = parameterResults?.[p.id]?.value;
              const trimmed = String(v).trim();
              if (isSpecialNumericResult(trimmed)) return false;
              const n = Number(trimmed);
              return Number.isNaN(n);
            })
            .map((p) => p.name);
          if (invalidNumeric.length > 0) {
            toast.error(`Invalid numeric value(s): ${invalidNumeric.join(", ")}`);
            return;
          }
        } else {
          const hasMain =
            mainResult.result_value?.trim() ||
            mainResult.numeric_value?.trim();
          if (!hasMain) {
            toast.error("Enter a result value before finalizing.");
            return;
          }
        }
      }

      // Aggregate status for parameterized tests
      const aggregateStatus = (() => {
        const statuses = testParameters
          .map((p) => parameterResults?.[p.id]?.status)
          .filter(Boolean);
        if (statuses.includes("critical")) return "critical";
        if (statuses.includes("high") || statuses.includes("low")) return statuses.includes("high") ? "high" : "low";
        if (statuses.includes("normal")) return "normal";
        return mainResult.status || "normal";
      })();

      const testResultPayload = {
        result_value: mainResult.result_value || null,
        numeric_value:
          mainResult.numeric_value && String(mainResult.numeric_value).trim() !== ""
            ? Number(mainResult.numeric_value)
            : null,
        unit: mainResult.unit || null,
        status: testParameters.length > 0 ? aggregateStatus : mainResult.status,
        comments: mainResult.comments || null,
        interpretation: mainResult.interpretation || null,
        laboratorist_id: user?.id || null,
      };

      // Build parameter payloads - ensure we include ALL parameters that have values or comments
      const paramPayloads = testParameters
        .map((p) => {
          const r = parameterResults?.[p.id];
          // If parameter result doesn't exist in state, it means user hasn't entered anything for it
          // This is fine - we only save parameters that have values or comments
          if (!r) {
            return null;
          }
          
          const raw = r?.value;
          const rawComments = r?.comments;
          const hasValue =
            raw !== null &&
            raw !== undefined &&
            String(raw).trim() !== "";
          const hasComments =
            rawComments !== null &&
            rawComments !== undefined &&
            String(rawComments).trim() !== "";

          // Allow saving parameter comments without a value (draft mode).
          // Finalization still validates at least one value above.
          if (!hasValue && !hasComments) return null;

          const ref = getActiveReferenceRange(p);
          const dataType = p.data_type || "numeric";
          const trimmed = hasValue ? String(raw).trim() : "";

          let resultValue = null;
          let stringValue = null;
          if (hasValue) {
            if (dataType === "numeric") {
              if (isSpecialNumericResult(trimmed)) {
                stringValue = trimmed;
              } else {
                const num = Number(trimmed);
                if (!Number.isNaN(num)) resultValue = num;
              }
            } else {
              stringValue = trimmed;
            }
          }

          const normalizedComments = hasComments ? String(rawComments).trim() : null;

          return {
            parameter_id: p.id,
            result_value: resultValue,
            string_value: stringValue,
            unit: r?.unit || ref?.unit || p.unit || null,
            reference_min: ref?.min_value ?? null,
            reference_max: ref?.max_value ?? null,
            status: r?.status || (hasValue ? "normal" : "unknown"),
            comments: normalizedComments,
          };
        })
        .filter(Boolean);
      
      // Log the payload being sent for debugging
      console.log("[ResultEntryForm] Parameter payload being sent:", {
        order_id: order?.id,
        is_final: isFinal,
        total_test_parameters: testParameters.length,
        parameter_results_in_state: Object.keys(parameterResults).length,
        payload_count: paramPayloads.length,
        parameter_ids_in_payload: paramPayloads.map(p => p.parameter_id),
        payload_details: paramPayloads.map(p => ({ 
          parameter_id: p.parameter_id, 
          has_value: p.result_value !== null || p.string_value !== null, 
          has_comments: p.comments !== null,
          value: p.result_value ?? p.string_value,
          comments: p.comments
        })),
      });
      
      // Verify we're sending all parameters that have data
      const parametersWithData = Object.entries(parameterResults).filter(([pid, r]) => {
        const v = r?.value;
        const c = r?.comments;
        return (v && String(v).trim() !== "") || (c && String(c).trim() !== "");
      });
      
      if (parametersWithData.length !== paramPayloads.length) {
        console.error("[ResultEntryForm] Mismatch: parameters with data vs payload count", {
          parameters_with_data: parametersWithData.length,
          payload_count: paramPayloads.length,
          missing_parameter_ids: parametersWithData
            .map(([pid]) => pid)
            .filter(pid => !paramPayloads.some(p => String(p.parameter_id) === String(pid))),
        });
      }

      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.log("[ResultEntryForm] submit payload", {
          order_id: order?.id,
          finalize: Boolean(isFinal),
          test_result: testResultPayload,
          parameter_results: paramPayloads,
        });
      }

      await dispatch(
        submitTestOrderResults({
          id: order.id,
          payload: {
            test_result: testResultPayload,
            parameter_results: paramPayloads,
            finalize: Boolean(isFinal),
          },
        })
      ).unwrap();

      // For draft saves, keep the form state as-is (don't fetch/hydrate immediately)
      // This ensures the values stay visible after saving
      if (!isFinal) {
        // Mark as dirty to prevent any hydration from overwriting our saved state
        markDirty();
        // Don't fetch results after draft save - keep the form state visible
        // eslint-disable-next-line no-console
        console.log("[ResultEntryForm] Draft saved - preserving form state", {
          order_id: order?.id,
          parameter_count: Object.keys(parameterResults).length,
          parameter_results: Object.entries(parameterResults).map(([id, r]) => ({
            param_id: id,
            value: r?.value,
            comments: r?.comments,
          })),
        });
      } else {
        // For finalize, fetch and hydrate normally
        // But keep form state dirty temporarily to prevent hydration from overwriting
        // with potentially incomplete data immediately after save
        const savedParameterCount = Object.keys(parameterResults).filter(
          (pid) => {
            const r = parameterResults[pid];
            const v = r?.value;
            const c = r?.comments;
            return (v && String(v).trim() !== "") || (c && String(c).trim() !== "");
          }
        ).length;
        
        // Fetch results but don't clear dirty flag yet - let hydration check the data first
        lastHydratedScopedResultsRef.current = null;
        const fetchResult = await dispatch(fetchTestOrderResults(order.id))
          .unwrap()
          .then((resp) => {
            const data = resp?.data ?? resp;
            const fetchedParameterCount = Array.isArray(data?.parameter_results)
              ? data.parameter_results.length
              : 0;
            
            // eslint-disable-next-line no-console
            console.log("[ResultEntryForm] fetch results after finalize", {
              order_id: order?.id,
              saved_parameter_count: savedParameterCount,
              fetched_parameter_count: fetchedParameterCount,
              parameter_ids_saved: Object.keys(parameterResults).filter(pid => {
                const r = parameterResults[pid];
                return (r?.value && String(r.value).trim() !== "") || (r?.comments && String(r.comments).trim() !== "");
              }),
              parameter_ids_fetched: Array.isArray(data?.parameter_results)
                ? data.parameter_results.map(pr => pr.parameter_id)
                : [],
              test_result_comments: data?.test_result?.comments,
              test_result_interpretation: data?.test_result?.interpretation,
            });
            
            // If fetched data is incomplete (fewer parameters than we saved),
            // keep the dirty flag to prevent overwriting form state
            if (fetchedParameterCount < savedParameterCount) {
              console.warn(`[ResultEntryForm] Fetched data incomplete: saved ${savedParameterCount} parameters but only got ${fetchedParameterCount}. Keeping form state.`);
              return { incomplete: true, data };
            }
            
            // Data looks complete, allow hydration
            clearDirty();
            return { incomplete: false, data };
          })
          .catch((err) => {
            console.error("[ResultEntryForm] Error fetching results after finalize:", err);
            // On error, keep form state (don't clear dirty)
            return { incomplete: true, error: err };
          });
        
        // If data was incomplete, clear dirty after a delay to allow backend to catch up
        if (fetchResult?.incomplete) {
          setTimeout(() => {
            clearDirty();
          }, 2000);
        }
      }

      // Show success toast
      toast.success(
        isFinal
          ? "Results verified and finalized successfully!"
          : "Results saved as draft successfully!",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      // Auto-close modal and reset form
      setTimeout(() => {
        if (autoCloseOnSave) {
          onClose?.();
        }
        onSaved?.({
          finalized: Boolean(isFinal),
          order_id: order?.id ?? null,
        });
      }, 1500);
    } catch (error) {
      console.error("Failed to save results:", error);

      let errorMessage = "Failed to save results. Please try again.";

      if (
        error.message?.includes("already exists") ||
        error.includes("already exists")
      ) {
        errorMessage =
          "Results for this order already exist. Please update the existing results instead.";
      } else if (
        error.message?.includes("Failed to update") ||
        error.includes("Failed to update")
      ) {
        errorMessage =
          "Failed to update results. The results may have been verified and cannot be modified.";
      } else if (error.message) {
        // Show the actual server error message if available
        errorMessage = `Failed to save results: ${error.message}`;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = (e) => {
    e.preventDefault();
    saveResults(false);
  };

  const handleFinalize = (e) => {
    e.preventDefault();
    // Explicit safety confirmation if any critical parameter
    const anyCritical = testParameters.some(
      (p) => parameterResults?.[p.id]?.status === "critical"
    );
    if (anyCritical) {
      setCriticalConfirmOpen(true);
      return;
    }
    saveResults(true);
  };

  // Check if we have any parameter results to save
  const hasParameterResults =
    testParameters.length > 0 &&
    Object.values(parameterResults).some((result) => {
      const v = result?.value;
      const hasValue = v !== null && v !== undefined && String(v).trim() !== "";
      const hasComment = result?.comments != null && String(result.comments).trim() !== "";
      return hasValue || hasComment;
    });

  // Check if we have any main result data to save
  const hasMainResultData =
    (mainResult.result_value !== null &&
      mainResult.result_value !== undefined &&
      String(mainResult.result_value).trim() !== "") ||
    (mainResult.numeric_value !== null &&
      mainResult.numeric_value !== undefined &&
      String(mainResult.numeric_value).trim() !== "") ||
    (mainResult.comments !== null &&
      mainResult.comments !== undefined &&
      String(mainResult.comments).trim() !== "") ||
    (mainResult.interpretation !== null &&
      mainResult.interpretation !== undefined &&
      String(mainResult.interpretation).trim() !== "");

  const canSave = hasParameterResults || hasMainResultData;
  const isSaveDisabled = loading || isSubmitting || isLocked || !canSave;

  const anyParamFilled =
    testParameters.length === 0
      ? false
      : testParameters.some((p) => {
          const v = parameterResults?.[p.id]?.value;
          return v !== null && v !== undefined && String(v).trim() !== "";
        });

  const isFinalizeDisabled =
    loading || isSubmitting || isLocked || (testParameters.length > 0 ? !anyParamFilled : !canSave);

  useEffect(() => {
    onActionStateChange?.({
      order_id: order?.id ?? null,
      isSaveDisabled,
      isFinalizeDisabled,
      isSubmitting,
      canSave,
      anyParamFilled,
    });
  }, [
    anyParamFilled,
    canSave,
    isFinalizeDisabled,
    isSaveDisabled,
    isSubmitting,
    onActionStateChange,
    order?.id,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: () => handleSaveDraft({ preventDefault: () => {} }),
      finalize: () => handleFinalize({ preventDefault: () => {} }),
      reset: () => resetForm(),
      getActionState: () => ({
        isSaveDisabled,
        isFinalizeDisabled,
        isSubmitting,
        canSave,
        anyParamFilled,
      }),
    })
  );

  // Enhanced dynamic questions display with question-answer pairing
  const renderDynamicQuestions = () => {
    const answers = order?.dynamic_answers;
    if (!answers || Object.keys(answers).length === 0) return null;

    // Normalize options to [{ value, label }, ...]
    const normalizeOptions = (opts) => {
      if (!opts) return [];
      if (Array.isArray(opts)) {
        // Accept ["Yes","No"] or [{value,label}]
        return opts.map((o) =>
          typeof o === "string"
            ? { value: o, label: o }
            : { value: o.value ?? o.label, label: o.label ?? String(o.value) }
        );
      }
      if (typeof opts === "object") {
        // Accept { yes: "Yes", no: "No" }
        return Object.entries(opts).map(([value, label]) => ({
          value,
          label: typeof label === "string" ? label : String(label),
        }));
      }
      return [];
    };

    const valueToLabel = (q, val) => {
      if (val == null || val === "") return "Not specified";
      const options = normalizeOptions(q?.options);
      const findLabel = (v) => {
        if (v === true) v = "true";
        if (v === false) v = "false";
        const found = options.find(
          (opt) => String(opt.value).toLowerCase() === String(v).toLowerCase()
        );
        return found ? found.label : String(v);
      };
      if (Array.isArray(val)) {
        return val.map(findLabel).join(", ");
      }
      if (typeof val === "string" && q?.field_type === "checkbox" && val.includes(",")) {
        return val
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .map(findLabel)
          .join(", ");
      }
      if (typeof val === "object") {
        // Fallback if stored as object
        try {
          return JSON.stringify(val);
        } catch {
          return String(val);
        }
      }
      return findLabel(val);
    };

    return (
      <div className="rounded-2xl border border-blue-100 bg-gradient-to-b from-blue-50 to-white shadow-sm">
        <div className="px-5 py-4 border-b border-blue-100">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-[#085DB6]">Ordering Answers</div>
              <div className="mt-1 text-xs text-blue-700/80">
                Read-only context captured at ordering time.
              </div>
            </div>
            <div className="text-[11px] text-blue-700/70 bg-white border border-blue-100 rounded-full px-3 py-1">
              Affects interpretation
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(answers).map(([id, val]) => {
              const q = questionMap[String(id)];
              const qText = q?.question_text || `Question #${id}`;
              const aText = valueToLabel(q, val);
              return (
                <div
                  key={id}
                  className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                >
                  <div className="text-xs font-semibold text-gray-700">{qText}</div>
                  <div className="mt-1 text-sm text-gray-900">
                    <span className="font-semibold">{aText}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render parameter with reference ranges and auto-calculation - FIXED INPUT HANDLING
  const renderParameterResults = () => {
    if (testParameters.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
          <div className="text-sm font-semibold text-gray-700">No parameters defined</div>
          <div className="mt-1 text-xs text-gray-500">This test uses a single overall result entry.</div>
        </div>
      );
    }

    const StatusPill = ({ status }) => {
      const s = getStatusStyle(status);
      return (
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${s.bg} ${s.color} ${s.border}`}
        >
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/70 border border-current/10 text-[11px] leading-none">
            {s.icon}
          </span>
          {s.label}
        </span>
      );
    };

    return (
      <div className={ui.card}>
        <div className={ui.cardHeader}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className={ui.sectionTitle}>Parameters</div>
              <div className={ui.sectionHint}>
                Enter what you have now — finalization requires at least one parameter.
              </div>
            </div>
            {isLocked ? (
              <div className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
                Locked (finalized)
              </div>
            ) : null}
          </div>
        </div>

        <div className={ui.cardBody}>
          <div className="space-y-4">
            {testParameters.map((param) => {
              const result = parameterResults[param.id] || {};
              const displayStatus = getDisplayStatus(param, result);
              const referenceRangeText = formatReferenceRange(param);
              const reason = param.applicable_reference_range_reason;

              const whyText = (() => {
                const bits = [];
                if (reason) bits.push(reason);
                if (order?.test_sample_type_id) bits.push(`TST #${order.test_sample_type_id}`);
                if (order?.gender) bits.push(`Gender: ${order.gender}`);
                if (order?.age !== null && order?.age !== undefined) bits.push(`Age: ${order.age}`);
                if (order?.sample_type) bits.push(`Sample: ${order.sample_type}`);
                return bits.join(" • ");
              })();

              return (
                <div key={param.id} className="rounded-2xl border border-gray-100 bg-white shadow-sm">
                  <div className="p-4 md:p-5">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                      <div className="lg:col-span-5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{param.name}</div>
                            <div className="mt-1 text-xs text-gray-500">
                              Reference: <span className="font-semibold text-gray-700">{referenceRangeText}</span>
                            </div>
                          </div>
                          <StatusPill status={displayStatus} />
                        </div>

                        {Array.isArray(param.reference_ranges) && param.reference_ranges.length > 1 ? (
                          <div className="mt-3">
                            <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                              Reference range
                            </label>
                            <select
                              value={selectedRangeIdByParamId?.[param.id] || ""}
                              onChange={(e) => setSelectedReferenceRange(param, e.target.value)}
                              disabled={isLocked}
                              className={ui.selectBase + " text-xs"}
                            >
                              {(param.reference_ranges || []).map((r) => (
                                <option key={r.id} value={String(r.id)}>
                                  {rangeLabel(r)}
                                </option>
                              ))}
                            </select>
                            {manualRangeByParamId?.[param.id] ? (
                              <div className="mt-1 text-[11px] text-gray-500">
                                Manual selection overrides default.
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {whyText ? (
                          <div className="mt-2 text-[11px] text-gray-500">
                            Selected by: <span className="font-semibold text-gray-700">{whyText}</span>
                          </div>
                        ) : null}

                        {param.description ? (
                          <div className="mt-2 text-[11px] text-gray-500 italic">{param.description}</div>
                        ) : null}
                      </div>

                      <div className="lg:col-span-4">
                        <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                          Value
                        </label>
                        {param.data_type === "boolean" ? (
                          <select
                            value={result.value || ""}
                            onChange={(e) =>
                              updateParameterResultValue(param.id, "value", e.target.value)
                            }
                            disabled={isLocked}
                            className={ui.selectBase}
                          >
                            <option value="">Select</option>
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={result.value || ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              if (param.data_type === "numeric") {
                                if (isAcceptedNumericResult(newValue)) {
                                  updateParameterResultValue(param.id, "value", newValue);
                                }
                              } else {
                                updateParameterResultValue(param.id, "value", newValue);
                              }
                            }}
                            onBlur={(e) => {
                              const trimmedValue = e.target.value.trim();
                              if (trimmedValue !== e.target.value) {
                                updateParameterResultValue(param.id, "value", trimmedValue);
                              }
                            }}
                            disabled={isLocked}
                            placeholder={param.data_type === "numeric" ? "e.g. 4.2 or <4" : "Enter value"}
                            className={ui.inputBase}
                          />
                        )}

                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className="text-xs text-gray-500">
                            Unit: <span className="font-semibold text-gray-700">{result.unit || param.unit || "N/A"}</span>
                          </div>
                          {displayStatus === "critical" ? (
                            <div className="text-xs font-semibold text-red-700">Critical — confirm on finalize</div>
                          ) : null}
                        </div>
                      </div>

                      <div className="lg:col-span-3">
                        <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                          Comments (optional)
                        </label>
                        <textarea
                          rows={3}
                          value={result.comments || ""}
                          onChange={(e) =>
                            updateParameterResultValue(param.id, "comments", e.target.value)
                          }
                          disabled={isLocked}
                          placeholder="Notes, observations, sample quality…"
                          className={ui.textareaBase}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Simple result entry for tests without parameters - FIXED INPUT HANDLING
  const renderSimpleResultEntry = () => (
    <div className={ui.card}>
      <div className={ui.cardHeader}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className={ui.sectionTitle}>Overall Result</div>
            <div className={ui.sectionHint}>Use this for tests without parameters.</div>
          </div>
          {isLocked ? (
            <div className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
              Locked (finalized)
            </div>
          ) : null}
        </div>
      </div>
      <div className={ui.cardBody}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">
              Result (text)
            </label>
            <input
              value={mainResult.result_value}
              onChange={(e) => {
                markDirty();
                setMainResult((prev) => ({
                  ...prev,
                  result_value: e.target.value,
                }));
              }}
              disabled={isLocked}
              className={ui.inputBase}
              placeholder="e.g. Positive / Negative / Trace"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">
              Numeric value
            </label>
            <input
              type="text"
              value={mainResult.numeric_value}
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue === "" || /^-?\d*\.?\d*$/.test(newValue)) {
                  markDirty();
                  setMainResult((prev) => ({
                    ...prev,
                    numeric_value: newValue,
                  }));
                }
              }}
              onBlur={(e) => {
                const trimmedValue = e.target.value.trim();
                if (trimmedValue !== e.target.value) {
                  markDirty();
                  setMainResult((prev) => ({
                    ...prev,
                    numeric_value: trimmedValue,
                  }));
                }
              }}
              className={ui.inputBase}
              placeholder="e.g. 4.2"
              disabled={isLocked}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">
              Unit
            </label>
            <input
              value={mainResult.unit}
              onChange={(e) => {
                markDirty();
                setMainResult((prev) => ({ ...prev, unit: e.target.value }));
              }}
              onBlur={(e) => {
                const trimmedValue = e.target.value.trim();
                if (trimmedValue !== e.target.value) {
                  markDirty();
                  setMainResult((prev) => ({
                    ...prev,
                    unit: trimmedValue,
                  }));
                }
              }}
              className={ui.inputBase}
              placeholder="e.g. mg/dL"
              disabled={isLocked}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">
              Status
            </label>
            <select
              value={mainResult.status}
              onChange={(e) => {
                markDirty();
                setMainResult((prev) => ({ ...prev, status: e.target.value }));
              }}
              disabled={isLocked}
              className={ui.selectBase}
            >
              <option value="normal">Normal</option>
              <option value="low">Low</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  // Render comments section
  const renderCommentsSection = () => (
    <div className={ui.card}>
      <div className={ui.cardHeader}>
        <div className={ui.sectionTitle}>Notes</div>
        <div className={ui.sectionHint}>Optional, but helpful for reporting and QA.</div>
      </div>
      <div className={ui.cardBody}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">
              Technical comments
            </label>
            <textarea
              rows={5}
              value={mainResult.comments}
              onChange={(e) => {
                markDirty();
                setMainResult((prev) => ({ ...prev, comments: e.target.value }));
              }}
              disabled={isLocked}
              className={ui.textareaBase}
              placeholder="Sample condition, instrument notes, pre-analytical issues…"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">
              Clinical interpretation
            </label>
            <textarea
              rows={5}
              value={mainResult.interpretation}
              onChange={(e) => {
                markDirty();
                setMainResult((prev) => ({
                  ...prev,
                  interpretation: e.target.value,
                }));
              }}
              disabled={isLocked}
              className={ui.textareaBase}
              placeholder="Summary of findings and clinical context…"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const finalizeHelperText = (() => {
    if (isLocked) return "This order is finalized and locked.";
    if (testParameters.length > 0 && !anyParamFilled) return "Finalize requires at least one parameter value.";
    if (testParameters.length === 0 && !canSave) return "Enter a result value (or comment) to save.";
    return null;
  })();

  const formatPatientAgeLabel = (o) => {
    if (!o) return "N/A";
    const dob = o.patient_date_of_birth || o.date_of_birth;
    if (dob) {
      const birth = new Date(dob);
      const today = new Date();
      if (!Number.isNaN(birth.getTime()) && today >= birth) {
        let years = today.getFullYear() - birth.getFullYear();
        let months = today.getMonth() - birth.getMonth();
        let days = today.getDate() - birth.getDate();

        if (days < 0) {
          months -= 1;
          const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
          days += prevMonth.getDate();
        }
        if (months < 0) {
          years -= 1;
          months += 12;
        }

        if (years > 0) {
          if (years < 2 && months > 0) return `${years}y ${months}m`;
          return `${years}y`;
        }
        if (months > 0) {
          if (days > 0) return `${months}m ${days}d`;
          return `${months}m`;
        }
        return `${days}d`;
      }
    }

    const a = o.age ?? o.patient_age;
    if (a === null || a === undefined || a === "") return "N/A";
    return `${a}y`;
  };

  return (
    <form onSubmit={(e) => e.preventDefault()} className="p-6 space-y-6">
      {!hasOrder ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
          Error: No order selected
        </div>
      ) : null}

      {showPatientContext ? (
        <div className="rounded-2xl border border-[#235F72]/10 bg-gradient-to-b from-[#235F72]/5 to-white shadow-sm">
          <div className="px-5 py-4 border-b border-[#235F72]/10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-[#235F72]">Patient & Order</div>
                <div className="mt-1 text-xs text-gray-500">Quick context for accurate interpretation.</div>
              </div>
              {isLocked ? (
                <div className="text-[11px] text-gray-600 bg-white border border-gray-200 rounded-full px-3 py-1">
                  Locked
                </div>
              ) : null}
            </div>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <div className="text-[11px] font-semibold text-gray-500">Patient</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">
                  {order?.patient_name || order?.full_name || "N/A"}
                </div>
                <div className="mt-1 text-xs text-gray-500">Card: {order?.card_number || "N/A"}</div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <div className="text-[11px] font-semibold text-gray-500">Age / Gender</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">
                  {formatPatientAgeLabel(order)} / {(order?.gender ?? "N/A")}
                </div>
                <div className="mt-1 text-xs text-gray-500">Sample: {order?.sample_type || "N/A"}</div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <div className="text-[11px] font-semibold text-gray-500">Test</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">
                  {testDetails?.name || `Test #${order?.test_id || "N/A"}`}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  TST: {order?.test_sample_type_id ? `#${order.test_sample_type_id}` : "N/A"}
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <div className="text-[11px] font-semibold text-gray-500">Order status</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">
                  {order?.status ? order.status.replace(/_/g, " ") : "N/A"}
                </div>
                <div className="mt-1 text-xs text-gray-500">Sample Type ID: {order?.sample_type_id ?? "N/A"}</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {renderDynamicQuestions()}

      {testParameters.length === 0 ? renderSimpleResultEntry() : renderParameterResults()}

      {renderCommentsSection()}

      {showActions ? (
        <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-[#235F72]">Actions</div>
                {finalizeHelperText ? (
                  <div className="mt-1 text-xs text-gray-500">{finalizeHelperText}</div>
                ) : (
                  <div className="mt-1 text-xs text-gray-500">Save draft anytime; finalize when ready.</div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={isSaveDisabled}
                  className={
                    "h-11 px-5 rounded-xl border text-sm font-semibold shadow-sm " +
                    (isSaveDisabled
                      ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")
                  }
                >
                  {isSubmitting ? "Saving…" : "Save draft"}
                </button>

                <button
                  type="button"
                  onClick={handleFinalize}
                  disabled={isFinalizeDisabled}
                  className={
                    "h-11 px-6 rounded-xl text-sm font-semibold shadow-sm " +
                    (isFinalizeDisabled
                      ? "bg-[#36F1A2]/30 text-[#235F72]/50 cursor-not-allowed"
                      : "bg-[#36F1A2] text-[#235F72] hover:bg-[#2dd191]")
                  }
                >
                  {isSubmitting ? "Processing…" : "Verify & finalize"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    onClose?.();
                  }}
                  className="h-11 px-5 rounded-xl bg-gray-700 text-white text-sm font-semibold shadow-sm hover:bg-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>

            {!canSave ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                Enter at least one result value or note to enable saving.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {criticalConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="p-6 border-b bg-red-50 border-red-100">
              <div className="text-lg font-semibold text-red-800">Critical result confirmation</div>
              <div className="text-sm text-red-800/80 mt-2">
                One or more parameters are marked <span className="font-semibold">CRITICAL</span>. Confirm before finalizing.
              </div>
            </div>
            <div className="p-6 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => setCriticalConfirmOpen(false)}
                className="h-11 px-5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
              >
                Review
              </button>
              <button
                type="button"
                onClick={() => {
                  setCriticalConfirmOpen(false);
                  saveResults(true);
                }}
                className="h-11 px-6 rounded-xl bg-[#36F1A2] text-[#235F72] text-sm font-semibold hover:bg-[#2dd191]"
              >
                Confirm finalize
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
});

export default ResultEntryForm;
